"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAppStore } from "@/lib/store";
import { apiGet, apiPatch, apiPost } from "@/lib/api-client";
import type { AdminUser } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import { roleLabel } from "@/components/booking/format";
import { useI18n } from "@/lib/i18n/provider";

const ROLES = ["USER", "OWNER", "ADMIN"] as const;
const LANGS = ["en", "fr", "de"] as const;

export function UserEditorModal() {
  const { userEditorOpen, editingUserId, closeUserEditor } = useAppStore();
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  // When the server generated the password, show it once before closing.
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "USER" as string,
    userGroup: "" as string,
    validated: true,
    active: true,
    preferredLang: "en" as string,
    password: "",
  });

  useEffect(() => {
    if (!userEditorOpen) return;
    if (editingUserId) {
      (async () => {
        try {
          const res = await apiGet<{ users: AdminUser[] }>("/api/admin/users");
          const u = res.users.find((x) => x.id === editingUserId);
          if (!u) throw new Error("User not found");
          setForm({
            username: u.username,
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            phone: u.phone ?? "",
            role: u.role,
            userGroup: u.userGroup ?? "",
            validated: u.validated,
            active: u.active,
            preferredLang: u.preferredLang,
            password: "",
          });
        } catch (e: any) {
          toast.error(e.message || t("toast.couldNotLoadUser"));
          closeUserEditor();
        }
      })();
    } else {
      setCreated(null);
      setCopied(false);
      setForm({
        username: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "USER",
        userGroup: "",
        validated: true,
        active: true,
        preferredLang: locale,
        password: "",
      });
    }
  }, [userEditorOpen, editingUserId, closeUserEditor, t, locale]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingUserId) {
        const patch: any = {
          username: form.username,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone || null,
          role: form.role,
          userGroup: form.userGroup || null,
          validated: form.validated,
          active: form.active,
          preferredLang: form.preferredLang,
        };
        if (form.password) patch.password = form.password;
        await apiPatch(`/api/admin/users/${editingUserId}`, patch);
        toast.success(t("toast.userUpdated"));
      } else {
        const res = await apiPost<{
          generatedPassword: string | null;
          user: { username: string };
        }>("/api/admin/users", {
          username: form.username,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone || null,
          role: form.role,
          userGroup: form.userGroup || null,
          preferredLang: form.preferredLang,
          password: form.password || null,
        });
        if (res.generatedPassword) {
          setCreated({ username: res.user.username, password: res.generatedPassword });
        } else {
          toast.success(t("toast.userCreated"));
          closeUserEditor();
        }
      }
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      closeUserEditor();
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotSave"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={userEditorOpen} onOpenChange={(o) => (o ? null : closeUserEditor())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingUserId ? t("userEditor.editTitle") : t("userEditor.newTitle")}
          </DialogTitle>
          <DialogDescription>{t("userEditor.description")}</DialogDescription>
        </DialogHeader>

          {created ? (
            <div className="grid gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-900 dark:bg-emerald-950">
                <p className="font-medium">{t("userEditor.createdTitle")}</p>
                <p className="mt-1 text-muted-foreground">{t("userEditor.createdHint")}</p>
              </div>
              <div className="grid gap-2">
                <div className="flex items-center gap-2">
                  <Input readOnly value={created.username} className="font-mono" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard?.writeText(created.username);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input readOnly value={created.password} className="font-mono" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard?.writeText(created.password);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard?.writeText(
                      created.username + " / " + created.password
                    );
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                >
                  {copied ? t("userEditor.copied") : t("userEditor.copy")}
                </Button>
                <Button type="button" onClick={closeUserEditor}>
                  {t("userEditor.done")}
                </Button>
              </DialogFooter>
            </div>
          ) : (
          <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label>{t("userEditor.username")}</Label>
            <Input
              required
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              placeholder={t("auth.field.usernamePlaceholder")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>
                {t("userEditor.firstName")}{" "}
                <span className="text-xs font-normal text-muted-foreground">{t("userEditor.optional")}</span>
              </Label>
              <Input
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>
                {t("userEditor.lastName")}{" "}
                <span className="text-xs font-normal text-muted-foreground">{t("userEditor.optional")}</span>
              </Label>
              <Input
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>
              {t("userEditor.email")}{" "}
              <span className="text-xs font-normal text-muted-foreground">{t("userEditor.optional")}</span>
            </Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("userEditor.phone")}</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder={t("auth.field.phonePlaceholder")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>{t("userEditor.role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {roleLabel(r, locale)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("userEditor.group")}</Label>
              <Select
                value={form.userGroup}
                onValueChange={(v) => setForm((f) => ({ ...f, userGroup: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("group.none")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FAMILY">{t("group.FAMILY")}</SelectItem>
                  <SelectItem value="GUEST">{t("group.GUEST")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>{t("lang.label")}</Label>
              <Select
                value={form.preferredLang}
                onValueChange={(v) => setForm((f) => ({ ...f, preferredLang: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGS.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editingUserId && (
              <div className="grid gap-1.5">
                <Label>{t("userEditor.validated")}</Label>
                <label className="flex h-9 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.validated}
                    onChange={(e) => setForm((f) => ({ ...f, validated: e.target.checked }))}
                    className="h-4 w-4 rounded border-input"
                  />
                  <span className="text-xs text-muted-foreground">{t("userEditor.validatedHint")}</span>
                </label>
              </div>
            )}
          </div>
          {editingUserId && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                className="h-4 w-4 rounded border-input"
              />
              {t("userEditor.active")}
            </label>
          )}
          <div className="grid gap-1.5">
            <Label>
              {t("userEditor.password")}{" "}
              {editingUserId ? (
                <span className="text-xs text-muted-foreground">{t("userEditor.passwordKeep")}</span>
              ) : (
                <span className="text-xs text-muted-foreground">{t("userEditor.passwordSet")}</span>
              )}
            </Label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={editingUserId ? t("userEditor.passwordPlaceholder") : t("userEditor.passwordSet")}
                className="font-mono"
              />
              {!editingUserId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
                    let out = "Hb-";
                    const arr = new Uint8Array(10);
                    crypto.getRandomValues(arr);
                    for (let i = 0; i < 10; i++) out += chars[arr[i] % chars.length];
                    setForm((f) => ({ ...f, password: out }));
                  }}
                >
                  {t("userEditor.generate")}
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeUserEditor}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy
                ? t("userEditor.saving")
                : editingUserId
                ? t("userEditor.saveChanges")
                : t("userEditor.create")}
            </Button>
          </DialogFooter>
        </form>
          )}
      </DialogContent>
    </Dialog>
  );
}
